import { Card } from "@/components/ui/card";
import { Upload, CheckCircle, Settings, Send, Globe, Book, FileCheck, Share, UserPlus, Sparkles, LucideIcon } from "lucide-react";
import { useWebsiteContent } from "@/contexts/WebsiteContentContext";
import { usePreview } from "@/contexts/PreviewContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEffect, useRef, useState } from "react";

// Icon mapping helper
const iconMap: Record<string, LucideIcon> = {
  upload: Upload,
  check: CheckCircle,
  settings: Settings,
  send: Send,
  globe: Globe,
  book: Book,
  fileCheck: FileCheck,
  share: Share,
  userPlus: UserPlus,
  sparkles: Sparkles,
};

const HowItWorks = () => {
  const { content, isLoading } = useWebsiteContent();
  const { previewContent, isPreviewMode } = usePreview();
  const { language } = useLanguage();
  const lang = (language.startsWith('en') ? 'en' : 'ar') as 'en' | 'ar';

  // Use preview content when in preview mode, otherwise use regular content
  const activeContent = (isPreviewMode && previewContent) ? previewContent : content;
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // All hooks must be called before any early returns
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add delay like statistics cards
            setTimeout(() => setIsVisible(true), 200);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    // Also show after content loads (fallback for refresh)
    if (!isLoading && content?.howItWorks?.steps?.length > 0) {
      setTimeout(() => setIsVisible(true), 500);
    }

    return () => observer.disconnect();
  }, [isLoading, content]);

  // Convert YouTube URL to embed format - This function doesn't use hooks so can be anywhere
  const getEmbedUrl = (url: string) => {
    if (!url) {
      return '';
    }

    // If already an embed URL, return as is
    if (url.includes('/embed/')) {
      return url;
    }

    // Extract video ID from various YouTube URL formats
    let videoId = '';

    // Format: https://www.youtube.com/watch?v=VIDEO_ID
    if (url.includes('watch?v=')) {
      videoId = url.split('watch?v=')[1].split('&')[0];
    }
    // Format: https://youtu.be/VIDEO_ID
    else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0];
    }

    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  // Don't render if content is still loading
  if (isLoading) {
    return (
      <section id="how-it-works" className="py-24 bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
      </section>
    );
  }

  const howItWorks = activeContent?.howItWorks;


  // Simplified safety check
  if (!howItWorks) {
    return (
      <section id="how-it-works" className="py-24 bg-white">
        <div className="container px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <p className="text-muted-foreground">How it works content not available</p>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} id="how-it-works" className="py-24 bg-white">
      <div className="container px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
            {howItWorks.title?.[lang] || 'How It Works'}
          </h2>
          <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
            {howItWorks.subtitle?.[lang] || 'Simple steps to publish your book'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {(howItWorks.steps || []).map((step, index) => {
            const IconComponent = iconMap[step.icon] || Upload;
            return (
              <Card
                key={index}
                className={`p-8 text-center border border-primary/20 bg-white shadow-md hover:shadow-xl hover:scale-105 hover:-translate-y-2 hover:border-cyan-400/40 hover:bg-gradient-to-br hover:from-cyan-100/70 hover:via-cyan-50/50 hover:to-teal-100/70 cursor-pointer group relative overflow-hidden rounded-2xl transition-all duration-1000 ease-out transform ${
                  isVisible
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-8 opacity-0'
                }`}
                style={{
                  transitionDelay: isVisible ? `${index * 500}ms` : '0ms'
                }}
              >
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="p-4 rounded-full bg-primary/15 border border-primary/30 group-hover:bg-primary/25 group-hover:scale-110 transition-all duration-300">
                      <IconComponent className="h-8 w-8 text-primary group-hover:text-primary group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary/20 rounded-full flex items-center justify-center group-hover:scale-125 transition-transform duration-300">
                      <span className="text-xs font-bold text-primary">{index + 1}</span>
                    </div>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
                  {step.title?.[lang] || 'Step Title'}
                </h3>
                <p className="text-sm text-foreground/70 leading-relaxed group-hover:text-foreground/80 transition-colors duration-300">
                  {step.description?.[lang] || 'Step description'}
                </p>
                {/* Background decoration */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
              </Card>
            );
          })}
        </div>

        {/* YouTube Video Player */}
        {howItWorks.videoUrl && (
          <div
            className={`mt-16 max-w-4xl mx-auto transition-all duration-1000 ease-out transform ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
            style={{
              transitionDelay: isVisible ? '2000ms' : '0ms'
            }}
          >
            <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl border-2 border-primary/20 hover:border-primary/40 transition-all duration-300">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={getEmbedUrl(howItWorks.videoUrl)}
                  title="How It Works Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default HowItWorks;
